/**
 * The seven states as one decision, and which controls each of them draws.
 *
 * Kept pure and out of the component because the control's whole job is to be true, and every way
 * it can lie is a case of collapsing two of these into one. On-unverified is its own state and not
 * a flavour of on: the server could not be asked, so claiming a working subscription would be a
 * guess. Repair is its own state and not a flavour of off: the browser is holding something that
 * will never deliver, and the visitor needs to be able to fix it.
 *
 * "On" is two facts. This browser holds a subscription, and the server holds a row for it. They
 * come apart in ordinary ways: the POST that registered it failed, the row was pruned when a push
 * service called the endpoint dead, or the visitor cleared the site. Asking only the browser is
 * what makes a control say on for a browser nothing can ever reach, which is the worst answer of
 * all, because it stops the person looking for the problem.
 */

export const VISITOR_PUSH_STATES = [
  'unavailable',
  'excluded',
  'unsupported',
  'blocked',
  'turning-on',
  'turning-off',
  'on',
  'on-unverified',
  'repair',
  'off-cleanup-pending',
  'off',
];

const TOGGLE = 'toggle';
const RETRY = 'retry';

/**
 * The browser let go and the server half did not answer.
 *
 * The endpoint is the whole of it. Once the browser has unsubscribed there is nothing left to
 * read from it, and this string is the only thing that still says which row on the server belongs
 * to this person. While it is here there is unfinished work, and that outranks every reason this
 * control has to draw nothing at all: an excluded viewer and a server with no keys both get a
 * blank page otherwise, with a live row behind it that a desk can still send to.
 *
 * It says nothing about somebody who never subscribed. They have no endpoint, so this is false
 * for them and they keep seeing nothing, which is the point of those two branches.
 */
function halfFinishedOff(f) {
  return f.hasSubscription === false && !!f.unconfirmedEndpoint;
}

/**
 * @param {{
 *   excluded?: boolean,
 *   configured?: boolean,
 *   support?: 'supported'|'unsupported'|'unsupported-ios-tab',
 *   permission?: 'default'|'granted'|'denied',
 *   busy?: null|'turning-on'|'turning-off',
 *   browserUnreadable?: boolean,
 *   subscriptionForAnotherKey?: boolean,
 *   hasSubscription?: boolean,
 *   known?: boolean|null,
 *   lastTurnOnFailed?: boolean,
 *   lastTurnOffFailed?: boolean,
 *   unconfirmedEndpoint?: string|null,
 * }} facts
 * @returns {string} one of VISITOR_PUSH_STATES
 */
export function visitorPushUiState(facts) {
  const f = facts || {};

  // This viewer is outside the audience of this host: the switch is off, the host is on neither
  // list, or it is admin only and they are not a sysadmin. Two outcomes and no third.
  //
  // Somebody who already said yes keeps a way out, and sees a sentence that does not promise
  // anything is still being delivered, because for them it is not. Everybody else sees nothing at
  // all, which is what makes taking a host off a list invisible to people who never used it.
  //
  // This outranks even `configured`, because the key is what a subscription is made with and this
  // state never makes one. An environment with no key still has to let its existing subscribers
  // out.
  if (f.excluded) {
    if (f.busy === 'turning-off') {
      return 'turning-off';
    }
    if (halfFinishedOff(f)) {
      return 'off-cleanup-pending';
    }
    if (f.lastTurnOffFailed) {
      return 'repair';
    }

    // A confirmed row, still. Unknown is not a yes here and that is deliberate: a browser on a
    // host that offers nothing may be holding a subscription for another brand entirely, and
    // "Je bent aangemeld" would then be about somebody else's list. The one case where this page
    // does know better is the line above, where it was this page that just tried to turn it off.
    return f.hasSubscription && f.known === true ? 'excluded' : 'unavailable';
  }

  // No key, no subscription, on any browser. A control here could only fail when pressed, and
  // the iOS sentence would walk somebody through adding the site to their home screen to reach
  // the same dead end, so this outranks even that.
  //
  // With the same exception as above, and for the same reason. Somebody who said yes while the
  // keys were there still holds a subscription and still has a row on the list. Pulling a key is
  // something an operator does on the server, and it may not lock a visitor into a list they were
  // promised they could leave. Deleting a row is not signed with anything, so the way out keeps
  // working on a server that can no longer send.
  if (f.configured === false) {
    // Turning off is the only thing that can be under way here, and while it is, the sentence
    // has to say so: these two branches sit above the busy check below, so without this line
    // pressing the one button on the screen changed nothing for as long as the server took.
    if (f.busy === 'turning-off') {
      return 'turning-off';
    }
    if (halfFinishedOff(f)) {
      return 'off-cleanup-pending';
    }
    if (f.lastTurnOffFailed) {
      return 'repair';
    }

    return f.hasSubscription && f.known === true ? 'excluded' : 'unavailable';
  }

  // Safari in a tab cannot subscribe on any iOS version, so nothing below this line can happen
  // there and no other fact changes the answer.
  if (f.support === 'unsupported-ios-tab') {
    return 'unsupported';
  }
  if (f.support !== 'supported') {
    return 'unavailable';
  }

  if (f.busy === 'turning-on' || f.busy === 'turning-off') {
    return f.busy;
  }

  // A browser that has refused notifications is not turning anything on.
  //
  // It can still be getting off the list, and that is why this no longer outranks everything. It
  // used to sit above the two lines below, and the result was a visitor whose browser let go while
  // the server half went unanswered: browser empty, row still there, and a screen with one greyed
  // out button and no way to finish. It also swallowed the turning off receipt, so pressing the
  // button changed nothing on screen for up to ten seconds.
  //
  // So: blocked, unless there is a half finished off, or a turn off that was refused
  // outright. The second one was the same screen before and after the press: no retry, no
  // word of failure, under a sentence that promises the button below takes you off the list.
  // Both of those have a control and a sentence of their own further down.
  //
  // The half finished off is asked by the same helper as the two branches at the top of this
  // function, under the same name. It used to ask a looser question of its own here: anything
  // this browser no longer holds and the server has not called gone. Nothing reaches that shape
  // today without an endpoint to finish with, so this is the comment and the code saying one
  // thing.
  if (f.permission === 'denied' && ! halfFinishedOff(f) && ! f.lastTurnOffFailed) {
    return 'blocked';
  }

  // The browser itself did not answer in time, so what it holds is unknown. Reporting off would
  // claim it holds nothing, and reporting on would claim the opposite; both are guesses about the
  // one half we normally can read for certain. Repair is the state that asks to try again.
  if (f.browserUnreadable) {
    return 'repair';
  }

  // The browser holds a subscription that was made against a different application server key.
  // Nothing this server sends can reach it, and the browser will refuse to make a new one until
  // that one is given back. It looks exactly like a working subscription from every other angle,
  // which is why it has to be named here: reporting on would be the one lie this control exists
  // to prevent, and the server would agree, because the row is real and the endpoint is real.
  if (f.subscriptionForAnotherKey) {
    return 'repair';
  }

  if (f.hasSubscription) {
    // A turn off where both halves refused. The browser still holds the subscription and the
    // server never answered, so on paper this is "on, not confirmed yet", and that is what this
    // used to say to somebody who had just pressed off: the sentence claimed notifications were
    // on, and the retry next to it asked the server again instead of finishing what they asked
    // for. Repair is the state that says something needs doing, and the retry there turns off.
    if (f.lastTurnOffFailed) {
      return 'repair';
    }
    if (f.known === true) {
      return 'on';
    }
    // Could not be asked. Not a reason to call a working subscription broken, and not a reason to
    // claim it works either.
    if (f.known === null || f.known === undefined) {
      return 'on-unverified';
    }

    return 'repair';
  }

  // Nothing is held anywhere, but the visitor pressed the button and it did not work. Saying
  // "notifications off" would be true and would tell them nothing about what just happened.
  if (f.lastTurnOnFailed) {
    return 'repair';
  }

  // The browser half is gone. Plain off needs the server half gone too, and known false is the
  // only answer that says so.
  if (f.known === false) {
    return 'off';
  }

  return 'off-cleanup-pending';
}

/**
 * Which controls are on screen in a given state. One source of truth, so a sentence can be checked
 * against it: three of the pinned sentences tell the visitor to try again, and a sentence naming a
 * control that is not drawn sends somebody hunting for a button that does not exist.
 *
 * @returns {string[]}
 */
export function visitorPushControlsFor(state, facts) {
  const f = facts || {};

  if (state === 'unsupported' || state === 'unavailable') {
    return [];
  }
  if (VISITOR_PUSH_STATES.indexOf(state) === -1) {
    return [];
  }

  // Where nothing is offered, nothing may offer to turn anything on.
  //
  // Two branches above reach a half finished off without anybody being invited to anything: a
  // viewer outside the audience, and a server with no keys. Both of those used to draw no block
  // at all, and giving them the cleanup state back also gave them its toggle, which reads
  // "Meldingen aan" for somebody who holds nothing and is live because permission is still
  // default there. Pressing it would ask the browser for permission and subscribe, the two
  // things those branches exist to never do, and the server would refuse it afterwards.
  //
  // The exception is the one this was built for: somebody who holds a subscription the server
  // confirms keeps the off button, because that is the way out.
  const nothingOffered = f.excluded === true || f.configured === false;
  if (nothingOffered && ! (f.hasSubscription === true && f.known === true)) {
    // Turning off is in that list because this helper is exported and asked on its own. The
    // component holds its control list still for the length of a press, so it no longer depends
    // on this answer, and that is exactly why the answer has to be right here rather than
    // implied there: asked about turning off where nothing is offered, the way out is what this
    // names, because the toggle would read "Meldingen aan" for somebody who holds nothing.
    return state === 'off-cleanup-pending' || state === 'repair' || state === 'turning-off'
      ? [RETRY]
      : [];
  }
  if (state === 'on-unverified' || state === 'repair' || state === 'off-cleanup-pending') {
    return [TOGGLE, RETRY];
  }

  // Excluded draws the off button and nothing else. Retry would offer to ask a server that has
  // already decided, and the sentence next to it does not ask anybody to try anything.
  if (state === 'excluded') {
    return [TOGGLE];
  }

  return [TOGGLE];
}
